import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  VStack,
  HStack,
  Avatar,
  Badge,
  Button,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Progress,
  Stat,
  StatLabel,
  StatNumber,
  Grid,
  GridItem,
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon,
  IconButton,
  Tooltip
} from '@chakra-ui/react';
import {
  FaUsers,
  FaSearch,
  FaUserTie,
  FaBalanceScale,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaEye,
  FaFilter
} from 'react-icons/fa';
import api from '../../../../services/api';

const TeamMemberRow = ({ member, onViewDetails }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'green';
      case 'busy': return 'yellow';
      case 'offline': return 'gray';
      default: return 'gray';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'gestor_juridico': return 'Gestor Jurídico';
      case 'juridico': return 'Analista Jurídico';
      default: return role;
    }
  };

  return (
    <Tr>
      <Td>
        <HStack>
          <Avatar name={member.name} size="sm" />
          <VStack align="start" spacing={0}>
            <Text fontWeight="medium">{member.name}</Text>
            <Text fontSize="xs" color="gray.500">{member.email}</Text>
          </VStack>
        </HStack>
      </Td>
      <Td>
        <Badge colorScheme={member.role === 'gestor_juridico' ? 'purple' : 'blue'}>
          {getRoleLabel(member.role)}
        </Badge>
      </Td>
      <Td>
        {member.gestorName ? (
          <HStack>
            <Icon as={FaUserTie} boxSize={3} color="purple.500" />
            <Text fontSize="sm">{member.gestorName}</Text>
          </HStack>
        ) : (
          <Text fontSize="sm" color="gray.400">-</Text>
        )}
      </Td>
      <Td isNumeric>{member.totalAssignments || 0}</Td>
      <Td isNumeric>{member.inProgress || 0}</Td>
      <Td isNumeric>
        <Text color="green.500" fontWeight="medium">
          {member.completed || 0}
        </Text>
      </Td>
      <Td isNumeric>
        <Text color="red.500" fontWeight="medium">
          {member.rejected || 0}
        </Text>
      </Td>
      <Td>
        <Progress 
          value={member.productivity || 0} 
          size="sm" 
          colorScheme={member.productivity > 70 ? 'green' : member.productivity > 40 ? 'yellow' : 'red'}
          width="80px"
        />
        <Text fontSize="xs" textAlign="center" mt={1}>
          {member.productivity || 0}%
        </Text>
      </Td>
      <Td>
        <Badge colorScheme={getStatusColor(member.status)}>
          {member.status === 'active' ? 'Ativo' : member.status === 'busy' ? 'Ocupado' : 'Offline'}
        </Badge>
      </Td>
      <Td>
        <Tooltip label="Ver detalhes">
          <IconButton
            icon={<FaEye />}
            size="sm"
            variant="ghost"
            onClick={() => onViewDetails(member)}
          />
        </Tooltip>
      </Td>
    </Tr>
  );
};

const FullTeamOverview = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [gestorFilter, setGestorFilter] = useState('all');
  const [stats, setStats] = useState({
    totalMembers: 0,
    gestores: 0,
    analistas: 0,
    avgProductivity: 0,
    totalCompleted: 0,
    totalInProgress: 0
  });
  const [gestoresList, setGestoresList] = useState([]);

  const bgColor = useColorModeValue('white', 'gray.700');

  useEffect(() => {
    fetchTeamData();
  }, []);

  useEffect(() => {
    filterMembers();
  }, [searchTerm, roleFilter, gestorFilter, teamMembers]);

  const fetchTeamData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get('/juridico/diretoria/department');
      const data = response.data || response;
      
      const members = Array.isArray(data.teamMembers) ? data.teamMembers : [];
      setTeamMembers(members);
      setFilteredMembers(members);
      
      // Extract unique gestores
      const uniqueGestores = [...new Set(members
        .filter(m => m.gestorName)
        .map(m => m.gestorName))];
      setGestoresList(uniqueGestores);
      
      // Calculate stats
      const gestoresCount = members.filter(m => m.role === 'gestor_juridico').length;
      const analistasCount = members.filter(m => m.role === 'juridico').length;
      const totalCompleted = members.reduce((acc, m) => acc + (m.completed || 0), 0);
      const totalInProgress = members.reduce((acc, m) => acc + (m.inProgress || 0), 0);
      const avgProductivity = members.length > 0
        ? members.reduce((acc, m) => acc + (m.productivity || 0), 0) / members.length
        : 0;
      
      setStats({
        totalMembers: members.length,
        gestores: gestoresCount,
        analistas: analistasCount,
        avgProductivity: Math.round(avgProductivity),
        totalCompleted,
        totalInProgress
      });
    } catch (err) {
      console.error('Error fetching team data:', err);
      setError('Erro ao carregar dados da equipe');
    } finally {
      setLoading(false);
    }
  };

  const filterMembers = () => {
    let filtered = [...teamMembers];
    
    if (searchTerm) {
      filtered = filtered.filter(member =>
        member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (roleFilter !== 'all') {
      filtered = filtered.filter(member => member.role === roleFilter);
    }
    
    if (gestorFilter !== 'all') {
      filtered = filtered.filter(member => member.gestorName === gestorFilter);
    }
    
    setFilteredMembers(filtered);
  };

  const handleViewDetails = (member) => {
    // Navigate to member details or open modal
    console.log('View details for:', member);
  };

  if (loading) {
    return (
      <Box py={10} textAlign="center">
        <Spinner size="xl" color="blue.500" />
        <Text mt={4}>Carregando equipe...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert status="error" rounded="md">
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <VStack spacing={6} align="stretch">
        <Box>
          <HStack spacing={3} mb={2}>
            <Icon as={FaUsers} boxSize={6} color="teal.600" />
            <Heading size="lg">Equipe Completa</Heading>
          </HStack>
          <Text color="gray.600">
            Todos os membros do departamento jurídico
          </Text>
        </Box>

        <Grid templateColumns="repeat(6, 1fr)" gap={4}>
          <GridItem>
            <Stat bg={bgColor} p={4} rounded="lg" shadow="sm">
              <StatLabel fontSize="xs">Total</StatLabel>
              <StatNumber fontSize="2xl">{stats.totalMembers}</StatNumber>
            </Stat>
          </GridItem>
          <GridItem>
            <Stat bg={bgColor} p={4} rounded="lg" shadow="sm">
              <StatLabel fontSize="xs">Gestores</StatLabel>
              <StatNumber fontSize="2xl" color="purple.500">{stats.gestores}</StatNumber>
            </Stat>
          </GridItem>
          <GridItem>
            <Stat bg={bgColor} p={4} rounded="lg" shadow="sm">
              <StatLabel fontSize="xs">Analistas</StatLabel>
              <StatNumber fontSize="2xl" color="blue.500">{stats.analistas}</StatNumber>
            </Stat>
          </GridItem>
          <GridItem>
            <Stat bg={bgColor} p={4} rounded="lg" shadow="sm">
              <StatLabel fontSize="xs">Produtividade</StatLabel>
              <StatNumber fontSize="2xl">{stats.avgProductivity}%</StatNumber>
            </Stat>
          </GridItem>
          <GridItem>
            <Stat bg={bgColor} p={4} rounded="lg" shadow="sm">
              <StatLabel fontSize="xs">Em Análise</StatLabel>
              <StatNumber fontSize="2xl" color="orange.500">{stats.totalInProgress}</StatNumber>
            </Stat>
          </GridItem>
          <GridItem>
            <Stat bg={bgColor} p={4} rounded="lg" shadow="sm">
              <StatLabel fontSize="xs">Concluídos</StatLabel>
              <StatNumber fontSize="2xl" color="green.500">{stats.totalCompleted}</StatNumber>
            </Stat>
          </GridItem>
        </Grid>

        <Box bg={bgColor} p={4} rounded="lg" shadow="sm">
          <HStack spacing={4} mb={4}>
            <InputGroup maxW="300px">
              <InputLeftElement pointerEvents="none">
                <FaSearch color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Buscar por nome ou email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
            
            <Select
              maxW="200px"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              leftIcon={<FaFilter />}
            >
              <option value="all">Todos os cargos</option>
              <option value="gestor_juridico">Gestores</option>
              <option value="juridico">Analistas</option>
            </Select>
            
            <Select
              maxW="200px"
              value={gestorFilter}
              onChange={(e) => setGestorFilter(e.target.value)}
            >
              <option value="all">Todos os gestores</option>
              {gestoresList.map(gestor => (
                <option key={gestor} value={gestor}>{gestor}</option>
              ))}
            </Select>
          </HStack>

          <TableContainer>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Membro</Th>
                  <Th>Cargo</Th>
                  <Th>Gestor</Th>
                  <Th isNumeric>Designações</Th>
                  <Th isNumeric>Em Análise</Th>
                  <Th isNumeric>Aprovados</Th>
                  <Th isNumeric>Reprovados</Th>
                  <Th>Produtividade</Th>
                  <Th>Status</Th>
                  <Th>Ações</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredMembers.length > 0 ? (
                  filteredMembers.map(member => (
                    <TeamMemberRow
                      key={member._id}
                      member={member}
                      onViewDetails={handleViewDetails}
                    />
                  ))
                ) : (
                  <Tr>
                    <Td colSpan={10} textAlign="center" py={8}>
                      <Text color="gray.500">
                        {searchTerm || roleFilter !== 'all' || gestorFilter !== 'all'
                          ? 'Nenhum membro encontrado com os filtros aplicados'
                          : 'Nenhum membro na equipe'}
                      </Text>
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
      </VStack>
    </Box>
  );
};

export default FullTeamOverview;